import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getVisitorId } from "../utils/analytics";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * AdBanner — kid-safe promo banner rendered from `/api/ads?slot=<slot>`.
 *
 * Compliance notes (Apple/Google Kids category):
 *   • No third-party network — images are served from our own origin as
 *     data URLs uploaded by the admin.
 *   • Every card carries a mandatory "Ad" chip so kids/parents can tell
 *     it apart from the app's own content.
 *   • Destination URLs are allowlist-validated server-side (internal
 *     routes preferred). External links open in a NEW tab with
 *     rel="noopener noreferrer" so the kid can never be trapped away
 *     from the app.
 *
 * Impression tracking (iter74):
 *   • One impression POST per (visitor, ad, session) — deduped via
 *     sessionStorage so SPA re-mounts don't inflate counts.
 *   • Click POST fired on every tap; keyed off the same visitor id.
 *
 * Slots: home_hero | home_secondary | games_row | games_row_bottom |
 *        cart_footer | notifications
 */
const _postAdEvent = (kind, ad_id, slot) => {
  try {
    fetch(`${BASE}/api/ads/${kind}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ad_id,
        slot,
        visitor_id: getVisitorId(),
      }),
      keepalive: true, // survive nav on click
    }).catch(() => {});
  } catch {
    /* fire and forget */
  }
};

const AdBanner = ({ slot, className = "" }) => {
  const [ad, setAd] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BASE}/api/ads?slot=${encodeURIComponent(slot)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled) return;
        const nextAd = j?.ad || null;
        setAd(nextAd);
        if (!nextAd) return;
        // Dedupe impressions to one per (visitor, ad) per browser session so
        // SPA re-mounts / router navigations don't inflate counts.
        try {
          const key = `ad_seen:${nextAd.ad_id}`;
          if (sessionStorage.getItem(key)) return;
          sessionStorage.setItem(key, "1");
        } catch {
          /* ignore storage errors */
        }
        _postAdEvent("impression", nextAd.ad_id, slot);
      })
      .catch(() => {
        /* best-effort */
      });
    return () => {
      cancelled = true;
    };
  }, [slot]);

  if (!ad) return null;

  const onClick = () => _postAdEvent("click", ad.ad_id, slot);
  const isExternal = ad.dest_url && !ad.dest_url.startsWith("/");
  const chip = (
    <span
      className="chunky-badge"
      data-testid={`ad-chip-${slot}`}
      style={{
        background: "#2b2b2b",
        color: "#FFF9E6",
        fontSize: 10,
        padding: "2px 8px",
        letterSpacing: 1,
      }}
    >
      Ad
    </span>
  );

  const body = (
    <div className="flex items-center gap-3 min-w-0">
      <img
        src={ad.image_url}
        alt={ad.headline}
        data-testid={`ad-image-${slot}`}
        loading="lazy"
        style={{
          width: 72,
          height: 72,
          borderRadius: 12,
          objectFit: "cover",
          border: "3px solid #2b2b2b",
          boxShadow: "0 3px 0 #2b2b2b",
          background: "#fff",
          flexShrink: 0,
        }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">{chip}</div>
        <div
          className="font-display text-base sm:text-lg leading-tight truncate"
          data-testid={`ad-headline-${slot}`}
        >
          {ad.headline}
        </div>
        {ad.cta_text ? (
          <div
            className="chunky-badge mt-1 inline-flex"
            style={{
              background: "#FFB400",
              color: "#2b2b2b",
              fontSize: 12,
              padding: "3px 10px",
            }}
            data-testid={`ad-cta-${slot}`}
          >
            {ad.cta_text} →
          </div>
        ) : null}
      </div>
    </div>
  );

  const wrapperClass = `chunky-card pushable p-3 sm:p-4 ${className}`;
  const wrapperStyle = {
    background:
      "linear-gradient(135deg, #FFF9E6 0%, #FFF3B0 45%, #FFECEC 100%)",
    display: "block",
    textDecoration: "none",
    color: "#2b2b2b",
  };

  if (!ad.dest_url) {
    return (
      <div
        className={wrapperClass}
        style={wrapperStyle}
        data-testid={`ad-banner-${slot}`}
        data-ad-id={ad.ad_id}
      >
        {body}
      </div>
    );
  }
  if (isExternal) {
    return (
      <a
        href={ad.dest_url}
        target="_blank"
        rel="noopener noreferrer"
        className={wrapperClass}
        style={wrapperStyle}
        data-testid={`ad-banner-${slot}`}
        data-ad-id={ad.ad_id}
        onClick={onClick}
      >
        {body}
      </a>
    );
  }
  return (
    <Link
      to={ad.dest_url}
      className={wrapperClass}
      style={wrapperStyle}
      data-testid={`ad-banner-${slot}`}
      data-ad-id={ad.ad_id}
      onClick={onClick}
    >
      {body}
    </Link>
  );
};

export default AdBanner;
